import { useState } from "react";

const faqs = [
  {
    q: "Can I go scuba diving from Bedarra Island?",
    a: "Yes. Bedarra Island Resort guests can take the Bedarra Water Taxi directly to Clump Point Jetty, Mission Beach, and join Mission Beach Dive Adventures for a full dive or snorkel day trip on the outer Great Barrier Reef. We recommend booking in advance to secure your preferred date.",
  },
  {
    q: "How far is the Great Barrier Reef from Bedarra Island?",
    a: "The outer Great Barrier Reef is approximately 45–60 minutes by boat from Clump Point Jetty aboard MV Mahi-Mahi. The water taxi from Bedarra Island travels directly to Clump Point Jetty — a scenic 20-minute crossing — making it an easy and rewarding day out from the resort.",
  },
  {
    q: "Do I need to be a certified diver to join a reef trip?",
    a: "No. Mission Beach Dive Adventures offers PADI Discover Scuba Diving for complete beginners — no prior experience required. Certified diver programs are available for Open Water and above, and guided snorkelling tours are also available for non-divers.",
  },
  {
    q: "How much does a reef day trip from Bedarra Island cost?",
    a: "There are three tours to choose from. The All Day Snorkel Tour is $449 per person and includes snorkel gear, a guided snorkel session, lunch, reef tax, water taxi transfer, and underwater photos. The Discover Scuba Diving experience (no certification needed) is $699 per person and includes all dive equipment, a PADI instructor one-on-one, lunch, reef tax, and water taxi transfer. The Certified Dive Tour is $799 per person and covers two reef sites, all dive equipment and wetsuit, a PADI dive guide, lunch, reef tax, and water taxi transfer. All prices are per person and fully inclusive — no hidden extras.",
  },
  {
    q: "What is included in a dive day trip with Mission Beach Dive Adventures?",
    a: "Every tour includes all necessary equipment, reef tax / Marine Park fee, lunch and refreshments, and your water taxi transfer between Bedarra Island and Clump Point Jetty. Certified Dive Tours include a PADI dive guide and two reef site dives. Discover Scuba Diving includes a dedicated one-on-one PADI instructor. The All Day Snorkel Tour also includes underwater photos — a great memento of your day on the reef.",
  },
  {
    q: "Is Mission Beach Dive Adventures PADI accredited?",
    a: "Yes. Mission Beach Dive Adventures is a fully PADI-accredited dive school and reef tour operator, based at Clump Point Jetty, Mission Beach, Queensland. All dive guides and instructors hold current PADI certifications.",
  },
  {
    q: "How do I book a scuba dive from Bedarra Island?",
    a: "Book directly at missionbeachdive.com.au or call us on 0455 957 263. We recommend booking at least 48 hours in advance to secure your preferred date, particularly during peak season.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="w-full bg-white py-24 px-6 md:px-10">
      <div className="max-w-3xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[3px] text-cyan-500 font-semibold mb-4">
            Common Questions
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-4">
            Real Questions. Straight Answers.
          </h2>
          <p className="text-gray-400 text-base leading-relaxed font-light">
            Everything Bedarra Island guests ask us before booking a scuba dive or reef day trip — answered straight.
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col divide-y divide-gray-100">
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={i} className="py-1">
                <button
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-start justify-between gap-4 py-5 text-left cursor-pointer group"
                >
                  <span className={`text-base font-semibold leading-snug transition-colors duration-200 ${isOpen ? "text-cyan-600" : "text-gray-800 group-hover:text-cyan-600"}`}>
                    {faq.q}
                  </span>
                  <span className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full border transition-all duration-300 mt-0.5 ${isOpen ? "border-cyan-400 bg-cyan-50 text-cyan-500 rotate-45" : "border-gray-200 text-gray-400 group-hover:border-cyan-300 group-hover:text-cyan-400"}`}>
                    <i className="ri-add-line text-base w-4 h-4 flex items-center justify-center" />
                  </span>
                </button>

                {/* Answer panel */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                >
                  <p className="text-gray-500 text-sm leading-relaxed pb-6 pr-4 md:pr-10">
                    {faq.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-gray-400 text-sm mb-5">
            Got one we didn&apos;t answer? Just call — we&apos;re on the water, not in a call centre.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm cursor-pointer hover:scale-105 transition-transform duration-300"
              style={{ boxShadow: "0 8px 24px rgba(249,115,22,0.35)" }}
            >
              <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
              Book Your Dive Day Trip
            </a>
            <a
              href="tel:0455957263"
              className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:border-gray-400 hover:text-gray-800 transition-all duration-200"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
              Call Us: 0455 957 263
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
